import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PresentMode({ presId, shareCode, onClose }) {
  const [pres, setPres] = useState(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const [direction, setDirection] = useState('next');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = shareCode
          ? await axios.get(`${API}/presentation/view/${shareCode}`)
          : await axios.get(`${API}/presentation/${presId}`);
        setPres(res.data);
      } catch (err) { alert('Failed to load'); onClose?.(); }
      finally { setLoading(false); }
    };
    load();
  }, [presId, shareCode]);

  const goTo = useCallback((idx, dir) => {
    if (!pres || transitioning) return;
    if (idx < 0 || idx >= pres.slides.length) return;
    setDirection(dir);
    setTransitioning(true);
    setTimeout(() => {
      setCurrentIdx(idx);
      setTimeout(() => setTransitioning(false), 50);
    }, 400);
  }, [pres, transitioning]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'Enter') { e.preventDefault(); goTo(currentIdx + 1, 'next'); }
      else if (e.key === 'ArrowLeft' || e.key === 'Backspace') { e.preventDefault(); goTo(currentIdx - 1, 'prev'); }
      else if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [currentIdx, goTo, onClose]);

  if (loading) return <div className="fixed inset-0 bg-black z-[100] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-white" /></div>;
  if (!pres?.slides?.length) return <div className="fixed inset-0 bg-black z-[100] flex items-center justify-center text-white">No slides</div>;

  const slide = pres.slides[currentIdx];
  const transition = slide?.transition || 'fade';

  const getTransitionClass = () => {
    if (!transitioning) return 'opacity-100 translate-x-0 translate-y-0 scale-100 rotate-0 blur-0';
    const exitDir = direction === 'next' ? 'exit' : 'exit-rev';
    const map = {
      fade: 'opacity-0',
      zoom: direction === 'next' ? 'scale-50 opacity-0' : 'scale-150 opacity-0',
      'slide-left': direction === 'next' ? '-translate-x-full opacity-0' : 'translate-x-full opacity-0',
      'slide-right': direction === 'next' ? 'translate-x-full opacity-0' : '-translate-x-full opacity-0',
      'slide-up': direction === 'next' ? '-translate-y-full opacity-0' : 'translate-y-full opacity-0',
      flip: '[transform:rotateY(90deg)] opacity-0',
      cube: direction === 'next' ? '[transform:perspective(800px)_rotateY(-90deg)] opacity-0' : '[transform:perspective(800px)_rotateY(90deg)] opacity-0',
      blur: 'blur-xl opacity-0 scale-105',
      parallax: direction === 'next' ? '-translate-x-1/4 scale-90 opacity-0' : 'translate-x-1/4 scale-90 opacity-0',
      morph: 'scale-110 opacity-0',
      none: '',
    };
    return map[transition] || 'opacity-0';
  };

  return (
    <div className="fixed inset-0 bg-black z-[100] cursor-none" data-testid="present-mode"
      onClick={e => { if (e.clientX > window.innerWidth / 2) goTo(currentIdx + 1, 'next'); else goTo(currentIdx - 1, 'prev'); }}
    >
      {/* CSS for custom transitions */}
      <style>{`
        .present-slide { transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1); }
        @keyframes slideEnter { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        .slide-enter { animation: slideEnter 0.4s ease-out; }
      `}</style>

      <div className={`present-slide w-full h-full flex items-center justify-center ${getTransitionClass()}`}>
        <div className="w-full h-full relative slide-enter" style={{ backgroundColor: slide.background?.value || '#1B3A6B' }}>
          {(slide.elements || []).map(elem => (
            <div key={elem.id} className="absolute" style={{
              left: `${elem.x}%`, top: `${elem.y}%`,
              width: `${elem.width}%`, height: `${elem.height}%`,
            }}>
              {elem.type === 'text' && (
                <div className="w-full h-full overflow-hidden whitespace-pre-wrap" style={{
                  fontSize: `clamp(12px, ${(elem.style?.fontSize || 20) / 16}vw, ${(elem.style?.fontSize || 20) * 2}px)`,
                  fontWeight: elem.style?.fontWeight || 'normal',
                  fontStyle: elem.style?.fontStyle || 'normal',
                  textAlign: elem.style?.textAlign || 'left',
                  color: elem.style?.color || '#fff',
                  lineHeight: elem.style?.lineHeight || 1.5,
                }}>
                  {elem.content}
                </div>
              )}
              {elem.type === 'image' && elem.content && (
                <img src={elem.content} alt="" className="w-full h-full object-contain" />
              )}
              {elem.type === 'shape' && (
                <div className="w-full h-full" style={{ backgroundColor: elem.style?.backgroundColor || '#B8860B', borderRadius: elem.style?.borderRadius || '0' }} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Progress bar */}
      <div className="fixed bottom-0 left-0 right-0 h-1 bg-white/10">
        <div className="h-full bg-[#B8860B] transition-all duration-300" style={{ width: `${((currentIdx + 1) / pres.slides.length) * 100}%` }} />
      </div>

      {/* Slide counter */}
      <div className="fixed bottom-3 right-4 text-white/40 text-sm font-mono select-none">
        {currentIdx + 1} / {pres.slides.length}
      </div>

      {/* Close button (visible on hover) */}
      <button onClick={e => { e.stopPropagation(); onClose?.(); }} className="fixed top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white/60 hover:text-white opacity-0 hover:opacity-100 transition-all" data-testid="present-close">
        <X className="w-5 h-5" />
      </button>
    </div>
  );
}
